import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { observePositionReconcileLatency } from '../../cache/position-entity.js';
import { sdkEvents } from '../events.js';
import {
  CONFIRMED_POSITION_TRANSITION_WINDOW_MS,
  hasPendingConfirmedPositionTransitions,
  releaseExpiredConfirmedPositionTransitions,
} from './confirmed-position-transition.js';
import { refetchRealtimeQuery } from './refetchRealtimeQuery.js';

/** How long a settle-time positions refetch may take before the watchdog
 *  declares it stuck and forces a cancel + retry. Generous vs the ~0.5s a
 *  healthy positions fetch takes, tight enough that a wedged query is
 *  repaired within one trading beat. */
export const POSITIONS_RECONCILE_WATCHDOG_MS = 10_000;
export const POSITIONS_RECONCILE_TRAILING_MS = 2_000;

/**
 * MOR-618 / MOR-873: reconcile every observed positions list to backend
 * truth after a trade settles.
 *
 * The optimistic upsert and the NATS prepend rows are client-side
 * ESTIMATES; `resultState`, the exact balance, released cost basis, and
 * realised PnL are backend-derived and absent from the trade wire, so a
 * real REST refetch MUST land after every trade or the estimates persist
 * as truth (a sold position keeps displaying as held — the MOR-873
 * incident).
 *
 * Why not `queryClient.invalidateQueries(...)`: TanStack latches the LAST
 * observer's `queryFn` onto the Query, and on these lists that is a
 * subscriber sentinel (`useLiveListData` mounts after the parent hook).
 * `invalidateQueries`' refetch runs the latched queryFn — the sentinel —
 * so no HTTP ever fires and the reconcile silently does nothing. The SDK's
 * `refetchRealtimeQuery` is the canonical path: it refetches through the
 * parent (data-owning) observer's own options, so the real queryFn runs.
 *
 * Watchdog: each refetch races a timeout. A healthy positions fetch takes
 * ~0.5s; if the promise hasn't settled in `POSITIONS_RECONCILE_WATCHDOG_MS`
 * the query is presumed wedged (e.g. a stuck retryer holding the dedupe
 * slot) — log a structured warning so the failure mode is VISIBLE, then
 * cancel the query (freeing the dedupe slot) and refetch once more. This
 * deliberately does not key on `dataUpdatedAt` or `isInvalidated`: both
 * are cleared by unrelated cache patches, so
 * only the refetch promise itself proves a REST round-trip completed.
 *
 * Confirmed-transition recovery: a short SDK-owned fence keeps pre-trade
 * projection rows from undoing the confirmed entity and its open/closed index
 * moves. The trailing fetch may acknowledge and clear it; otherwise one final
 * bounded fetch runs after the fence emits its terminal diagnostic.
 */
export function reconcilePositionsAfterTrade(queryClient: QueryClient): void {
  // Mark every positions list stale without letting TanStack refetch
  // through the Query-latched (subscriber) queryFn.
  queryClient.invalidateQueries({
    predicate: (q: { queryKey: QueryKey }): boolean => isPositionsListKey(q.queryKey),
    refetchType: 'none',
  });

  const targets = queryClient
    .getQueryCache()
    .getAll()
    .filter((q) => isPositionsListKey(q.queryKey) && q.observers.length > 0);
  scheduleConfirmedTransitionRecovery(
    queryClient,
    targets.map((query) => ({ key: query.queryKey, queryHash: query.queryHash })),
  );

  for (const query of targets) {
    const key = query.queryKey;
    const refetch = (async (): Promise<'settled'> => {
      const startedAt = Date.now();
      try {
        await refetchRealtimeQuery(queryClient, key);
      } catch (err) {
        recordPositionReconcileFailure('settle_refetch_failed', {
          queryHash: query.queryHash.slice(0, 300),
          error: err instanceof Error ? err.message.slice(0, 200) : String(err).slice(0, 200),
        });
      } finally {
        observePositionReconcileLatency(Date.now() - startedAt);
      }
      scheduleTrailingPositionRefetch(queryClient, key, query.queryHash);
      return 'settled';
    })();
    const timeout = new Promise<'timeout'>((resolve) => {
      setTimeout(() => {
        resolve('timeout');
      }, POSITIONS_RECONCILE_WATCHDOG_MS);
    });
    void Promise.race([refetch, timeout]).then((outcome) => {
      if (outcome !== 'timeout') return;
      recordPositionReconcileFailure('watchdog_timeout', {
        queryHash: query.queryHash.slice(0, 300),
        fetchStatus: query.state.fetchStatus,
        watchdogMs: POSITIONS_RECONCILE_WATCHDOG_MS,
      });
      void queryClient
        .cancelQueries({ queryKey: key, exact: true })
        .then(async () => {
          const retryStartedAt = Date.now();
          try {
            await refetchRealtimeQuery(queryClient, key);
          } finally {
            observePositionReconcileLatency(Date.now() - retryStartedAt);
          }
        })
        .catch((err) => {
          recordPositionReconcileFailure('watchdog_retry_failed', {
            queryHash: query.queryHash.slice(0, 300),
            error: err instanceof Error ? err.message.slice(0, 200) : String(err).slice(0, 200),
          });
        });
    });
  }
}

function scheduleConfirmedTransitionRecovery(
  queryClient: QueryClient,
  targets: readonly { readonly key: QueryKey; readonly queryHash: string }[],
): void {
  setTimeout(() => {
    if (!hasPendingConfirmedPositionTransitions(queryClient)) return;
    releaseExpiredConfirmedPositionTransitions(queryClient);
    void Promise.all(
      targets.map((target) => recoverConfirmedTransitionTarget(queryClient, target)),
    );
  }, CONFIRMED_POSITION_TRANSITION_WINDOW_MS);
}

async function recoverConfirmedTransitionTarget(
  queryClient: QueryClient,
  target: { readonly key: QueryKey; readonly queryHash: string },
): Promise<void> {
  const startedAt = Date.now();
  const refetch = refetchRealtimeQuery(queryClient, target.key)
    .catch((err) => {
      recordPositionReconcileFailure('confirmed_transition_failed', {
        queryHash: target.queryHash.slice(0, 300),
        error: err instanceof Error ? err.message.slice(0, 200) : String(err).slice(0, 200),
      });
    })
    .finally(() => {
      observePositionReconcileLatency(Date.now() - startedAt);
    });
  const timeout = new Promise<'timeout'>((resolve) => {
    setTimeout(() => resolve('timeout'), POSITIONS_RECONCILE_WATCHDOG_MS);
  });
  const outcome = await Promise.race([refetch.then(() => 'settled' as const), timeout]);
  if (outcome === 'timeout') {
    recordPositionReconcileFailure('confirmed_transition_timeout', {
      queryHash: target.queryHash.slice(0, 300),
      watchdogMs: POSITIONS_RECONCILE_WATCHDOG_MS,
    });
  }
}

function scheduleTrailingPositionRefetch(
  queryClient: QueryClient,
  key: QueryKey,
  queryHash: string,
): void {
  setTimeout(() => {
    const startedAt = Date.now();
    void refetchRealtimeQuery(queryClient, key)
      .catch((err) => {
        recordPositionReconcileFailure('trailing_refetch_failed', {
          queryHash: queryHash.slice(0, 300),
          error: err instanceof Error ? err.message.slice(0, 200) : String(err).slice(0, 200),
        });
      })
      .finally(() => {
        observePositionReconcileLatency(Date.now() - startedAt);
      });
  }, POSITIONS_RECONCILE_TRAILING_MS);
}

function isPositionsListKey(queryKey: QueryKey): boolean {
  const params = queryKey[3];
  return (
    queryKey[0] === 'users' &&
    queryKey[1] === 'list' &&
    typeof params === 'object' &&
    params !== null &&
    'includeZero' in params
  );
}

function recordPositionReconcileFailure(
  reason: string,
  attributes: Readonly<Record<string, string | number>>,
): void {
  sdkEvents.emit('position-reconcile:diagnostic', { reason, ...attributes });
}
